// FPStandings.js
// Franchise Player — standings. Two views:
//  • This week (default): rank, entry, this week's points (live), projected finish and
//    season total. Sort by any of the three; tap an entry to see players whose games
//    have started.
//  • History: every week's points side by side (scroll sideways), entry pinned left.
// Scores update every 5 minutes during games.

import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { FP_ACCENT, FP_ACCENT_DIM } from "./fp-theme";
import { TeamLogo, PosBadge, fmtPts, GameLine, scoreboardByTeam, agoText, LIVE_CSS } from "./fp-stats";

const SLOT_ORDER = ["QB", "RB1", "RB2", "WR1", "WR2", "TE", "FLEX1", "FLEX2", "DEF"];
const CARD = "var(--navy-card)"; // pinned History column matches the card behind it

// Rank with ties (1, 2, 2, 4).
function withRanks(rows, key) {
  const sorted = [...rows].sort((a, b) => (b[key] ?? -Infinity) - (a[key] ?? -Infinity));
  sorted.forEach((r, i) => { r.rank = i > 0 && sorted[i - 1][key] === r[key] ? sorted[i - 1].rank : i + 1; });
  return sorted;
}

export default function FPStandings({ league, user }) {
  const [season, setSeason] = useState(null);
  const [weeks, setWeeks] = useState({});
  const [view, setView] = useState("week"); // "week" | "history"
  const [sortBy, setSortBy] = useState("season"); // "season" | "week" | "proj"
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const u1 = onSnapshot(doc(db, "fp_seasonStandings", league.id), (s) => setSeason(s.exists() ? s.data() : false), () => setSeason(false));
    const u2 = onSnapshot(query(collection(db, "fp_scores"), where("leagueId", "==", league.id)), (snap) => {
      const m = {};
      snap.docs.forEach((d) => { const w = d.data(); m[w.week] = w; });
      setWeeks(m);
    }, () => {});
    return () => { u1(); u2(); };
  }, [league.id]);

  const weekNums = Object.keys(weeks).map(Number).sort((a, b) => b - a);
  const curWeek = weekNums[0] ?? null;

  // Live scores for the current week
  const [scoreboard, setScoreboard] = useState(null);
  useEffect(() => {
    if (!curWeek) { setScoreboard(null); return; }
    const unsub = onSnapshot(doc(db, "fp_scoreboard", `${league.season}_${curWeek}`), (s) => setScoreboard(s.exists() ? s.data() : null), () => setScoreboard(null));
    return unsub;
  }, [curWeek, league.season]);
  const sbTeam = scoreboardByTeam(scoreboard);
  const anyLive = Object.values(scoreboard?.games || {}).some((g) => g.status === "live");
  const gameFor = (team) => {
    const g = sbTeam[team];
    return g ? <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginLeft: 6 }}><GameLine team={team} opp={g.away === team ? g.home : g.away} home={g.home === team} kickoff={g.kickoff} sbGame={g} compact /></span> : null;
  };

  // One row per entry: season total plus this week's points and projected finish.
  const rows = useMemo(() => {
    const cur = weeks[curWeek]?.entries || {};
    const seasonEntries = season?.entries || {};
    const ids = new Set([...Object.keys(seasonEntries), ...Object.keys(cur)]);
    return [...ids].map((id) => {
      const s = seasonEntries[id] || {}, w = cur[id] || {};
      return {
        id, name: w.name || s.name, ownerName: w.ownerName || s.ownerName, ownerUid: w.ownerUid || s.ownerUid,
        captain: w.captain || s.captain, captainBenched: !!w.captainBenched, captainPoints: w.captainPoints || 0,
        hasLineup: !!w.hasLineup, visible: w.visible || {},
        week: w.points ?? 0, proj: w.proj ?? null, season: s.total ?? 0, weeks: s.weeks || {},
      };
    });
  }, [season, weeks, curWeek]);

  const weekRows = useMemo(() => withRanks(rows, sortBy), [rows, sortBy]);
  const historyRows = useMemo(() => withRanks(rows, "season"), [rows]);
  const weekTop = useMemo(() => {
    const top = {};
    for (const w of weekNums) top[w] = Math.max(...Object.values(weeks[w]?.entries || {}).map((e) => (e.hasLineup ? e.points || 0 : -Infinity)));
    return top;
  }, [weeks]); // eslint-disable-line react-hooks/exhaustive-deps

  if (season === null) return <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading standings...</p>;
  if (!weekNums.length) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>📈</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1, marginBottom: 6 }}>NO SCORES YET</div>
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, maxWidth: 440, margin: "0 auto" }}>
          Standings start when week {league.startWeek}'s games kick off. Scores update every 5 minutes during games.
        </p>
      </div>
    );
  }

  const SortHead = ({ k, children, title }) => (
    <th style={{ ...thS, cursor: "pointer", color: sortBy === k ? "white" : thS.color }} onClick={() => setSortBy(k)} title={title} aria-sort={sortBy === k ? "descending" : "none"}>
      <span style={{ borderBottom: sortBy === k ? `2px solid ${FP_ACCENT}` : "2px solid transparent", paddingBottom: 2 }}>{children}</span>
    </th>
  );

  return (
    <div className="animate-fade-in">
      <style>{LIVE_CSS}</style>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button onClick={() => setView("week")} className={view === "week" ? "btn btn-fp btn-sm" : "btn btn-ghost btn-sm"}>This week</button>
        <button onClick={() => setView("history")} className={view === "history" ? "btn btn-fp btn-sm" : "btn btn-ghost btn-sm"}>History</button>
      </div>

      {view === "week" ? (
        <div className="card" style={{ padding: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thS, width: 30 }}>#</th>
                <th style={{ ...thS, textAlign: "left" }}>Entry</th>
                <SortHead k="week" title={`Points so far in week ${curWeek}`}>
                  {anyLive && <span style={{ color: "#F87171", animation: "fpLivePulse 1.6s ease-in-out infinite", display: "inline-block", marginRight: 3 }}>●</span>}Wk {curWeek}
                </SortHead>
                <SortHead k="proj" title="Projected finish: actual points where games are done, plus projections for the rest">Proj</SortHead>
                <SortHead k="season" title="Season total">Season</SortHead>
              </tr>
            </thead>
            <tbody>
              {weekRows.map((r) => {
                const mine = r.ownerUid === user.uid;
                const isOpen = open === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr role="button" tabIndex={0} onClick={() => setOpen(isOpen ? null : r.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(isOpen ? null : r.id); } }}
                      style={{ background: mine ? FP_ACCENT_DIM : "transparent", cursor: "pointer", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ ...tdS, fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, textAlign: "center", color: r.rank <= 3 ? FP_ACCENT : "rgba(255,255,255,0.5)" }}>{r.rank}</td>
                      <td style={{ ...tdS, textAlign: "left", minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
                        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                          {r.ownerName}
                          {r.captain?.name && <>{" · "}<TeamLogo team={r.captain.team} size={14} /> {r.captain.name}{r.captainBenched ? " (benched)" : ""}</>}
                          {!r.hasLineup && " · no lineup"}
                        </div>
                      </td>
                      <td style={{ ...numS, fontWeight: sortBy === "week" ? 800 : 600 }}>{fmtPts(r.week)}</td>
                      <td style={{ ...numS, color: "rgba(255,255,255,0.6)", fontWeight: sortBy === "proj" ? 800 : 400 }}>{r.proj != null ? fmtPts(r.proj) : "—"}</td>
                      <td style={{ ...numS, fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 0.5, color: sortBy === "season" ? "white" : "rgba(255,255,255,0.75)" }}>{fmtPts(r.season)}</td>
                    </tr>
                    {isOpen && (
                      <tr><td />
                        <td colSpan={4} style={{ padding: "2px 8px 10px", fontSize: 12 }}>
                          {!r.captainBenched && r.captain?.name && (
                            <div style={rowS}><span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}><PosBadge pos={r.captain.pos} small /> <TeamLogo team={r.captain.team} size={14} /> {r.captain.name} <b style={{ color: FP_ACCENT }}>1.25x</b>{gameFor(r.captain.team)}</span><b>{fmtPts(r.captainPoints)}</b></div>
                          )}
                          {SLOT_ORDER.filter((s) => r.visible?.[s]).map((s) => {
                            const p = r.visible[s];
                            return <div key={s} style={rowS}><span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}><PosBadge pos={p.pos} small /> <TeamLogo team={p.team} size={14} /> {p.name}{gameFor(p.team)}</span><b>{fmtPts(p.pts)}</b></div>;
                          })}
                          {Object.keys(r.visible || {}).length < SLOT_ORDER.length - 1 && (
                            <div style={{ color: "rgba(255,255,255,0.35)", marginTop: 4 }}>Other players appear once their games start.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...thS, ...stickyS, background: CARD, textAlign: "left", paddingLeft: 12 }}>Entry</th>
                <th style={{ ...thS, color: "white" }}>Season</th>
                {weekNums.map((w) => <th key={w} style={thS}>Wk {w}</th>)}
              </tr>
            </thead>
            <tbody>
              {historyRows.map((r) => {
                const mine = r.ownerUid === user.uid;
                return (
                  <tr key={r.id}>
                    <td style={{ ...tdS, ...stickyS, textAlign: "left", paddingLeft: 12, borderTop: "1px solid rgba(255,255,255,0.05)",
                      background: mine ? `linear-gradient(${FP_ACCENT_DIM}, ${FP_ACCENT_DIM}), ${CARD}` : CARD }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, width: 20, color: r.rank <= 3 ? FP_ACCENT : "rgba(255,255,255,0.5)" }}>{r.rank}</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>{r.name}</div>
                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap" }}>{r.ownerName}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...numS, fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, borderTop: "1px solid rgba(255,255,255,0.05)", background: mine ? FP_ACCENT_DIM : "transparent" }}>{fmtPts(r.season)}</td>
                    {weekNums.map((w) => {
                      const e = weeks[w]?.entries?.[r.id];
                      const pts = e?.hasLineup ? e.points || 0 : null;
                      const top = pts != null && pts === weekTop[w];
                      return (
                        <td key={w} title={top ? `Top score in week ${w}` : undefined}
                          style={{ ...numS, borderTop: "1px solid rgba(255,255,255,0.05)", background: mine ? FP_ACCENT_DIM : "transparent",
                            color: pts == null ? "rgba(255,255,255,0.25)" : top ? FP_ACCENT : "rgba(255,255,255,0.8)", fontWeight: top ? 800 : 400 }}>
                          {pts == null ? "—" : <>{top && "🏆 "}{fmtPts(pts)}</>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 10, lineHeight: 1.6 }}>
        {view === "week"
          ? <>Tap a column to sort; tap an entry to see players whose games have started. Proj = actual points where games are done, points so far plus the rest of the projection for games in progress, and projections for games yet to play.</>
          : <>Points by week, newest first. 🏆 marks each week's top score. Scroll sideways for earlier weeks.</>}
        {" "}Scores update every 5 minutes during games{anyLive && scoreboard?.updatedAt ? ` (${agoText(scoreboard.updatedAt)})` : ""}. Stat corrections are applied Tuesday morning.
      </p>
    </div>
  );
}

const thS = { padding: "6px 8px", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "rgba(255,255,255,0.4)", textAlign: "right", whiteSpace: "nowrap" };
const tdS = { padding: "9px 8px", verticalAlign: "middle" };
const numS = { ...tdS, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", fontSize: 14 };
const stickyS = { position: "sticky", left: 0, zIndex: 1, minWidth: 170 };
const rowS = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" };
